import React, { useState, useEffect } from 'react';
import { Building2, Globe, Users } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CustomersPartners = () => {
  const [customers, setCustomers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [customersResponse, partnersResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customers`),
        axios.get(`${BACKEND_URL}/api/partners`)
      ]);
      setCustomers(customersResponse.data);
      setPartners(partnersResponse.data);
    } catch (error) {
      console.error('Failed to load customers/partners:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section id="customers-partners" className="py-20 bg-gradient-to-br from-white via-[#176472]/5 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center">
            <p className="text-slate-500">Loading...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="customers-partners" className="py-20 bg-gradient-to-br from-white via-[#176472]/5 to-white">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-6xl font-bold text-slate-900 mb-4">
            Our <span className="bg-gradient-to-r from-[#003366] via-[#339933] to-[#003366] bg-clip-text text-transparent">Customers & Partners</span>
          </h2>
          <p className="text-lg text-slate-600">
            Trusted collaborations driving sustainable development
          </p>
        </div>

        {/* Customers Section */}
        {customers.length > 0 && (
          <div className="mb-16">
            <div className="flex items-center justify-center space-x-3 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-[#003366] to-[#339933] rounded-xl flex items-center justify-center shadow-lg">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-slate-900">Our Customers</h3>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {customers.map((customer) => (
                <Card
                  key={customer.id}
                  className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 bg-white group"
                >
                  <CardContent className="p-6 flex flex-col items-center justify-center h-full min-h-[180px]">
                    {customer.website ? (
                      <a href={customer.website} target="_blank" rel="noopener noreferrer" className="w-full">
                        {customer.logo ? (
                          <img
                            src={customer.logo.startsWith('http') ? customer.logo : `${BACKEND_URL}${customer.logo}`}
                            alt={customer.name}
                            className="w-full h-24 object-contain mb-4 grayscale group-hover:grayscale-0 transition-all duration-300 cursor-pointer"
                          />
                        ) : (
                          <div className="w-full h-24 bg-gradient-to-br from-[#1E4E9E]/10 to-[#00B050]/10 rounded-lg flex items-center justify-center mb-4 cursor-pointer">
                            <Building2 className="w-12 h-12 text-[#003366]/30" />
                          </div>
                        )}
                      </a>
                    ) : (
                      <>
                        {customer.logo ? (
                          <img
                            src={customer.logo.startsWith('http') ? customer.logo : `${BACKEND_URL}${customer.logo}`}
                            alt={customer.name}
                            className="w-full h-24 object-contain mb-4 grayscale group-hover:grayscale-0 transition-all duration-300"
                          />
                        ) : (
                          <div className="w-full h-24 bg-gradient-to-br from-[#1E4E9E]/10 to-[#00B050]/10 rounded-lg flex items-center justify-center mb-4">
                            <Building2 className="w-12 h-12 text-[#003366]/30" />
                          </div>
                        )}
                      </>
                    )}
                    <p className="text-sm font-semibold text-slate-700 text-center">{customer.name}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Partners Section */}
        {partners.length > 0 && (
          <div>
            <div className="flex items-center justify-center space-x-3 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-[#339933] to-[#003366] rounded-xl flex items-center justify-center shadow-lg">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-slate-900">Our Partners</h3>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {partners.map((partner) => (
                <Card
                  key={partner.id}
                  className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 bg-white group"
                >
                  <CardContent className="p-6 flex flex-col items-center justify-center h-full min-h-[180px]">
                    {partner.website ? (
                      <a href={partner.website} target="_blank" rel="noopener noreferrer" className="w-full">
                        {partner.logo ? (
                          <img
                            src={partner.logo.startsWith('http') ? partner.logo : `${BACKEND_URL}${partner.logo}`}
                            alt={partner.name}
                            className="w-full h-24 object-contain mb-4 grayscale group-hover:grayscale-0 transition-all duration-300 cursor-pointer"
                          />
                        ) : (
                          <div className="w-full h-24 bg-gradient-to-br from-[#00B050]/10 to-[#1E4E9E]/10 rounded-lg flex items-center justify-center mb-4 cursor-pointer">
                            <Globe className="w-12 h-12 text-[#74C65A]/30" />
                          </div>
                        )}
                      </a>
                    ) : (
                      <>
                        {partner.logo ? (
                          <img
                            src={partner.logo.startsWith('http') ? partner.logo : `${BACKEND_URL}${partner.logo}`}
                            alt={partner.name}
                            className="w-full h-24 object-contain mb-4 grayscale group-hover:grayscale-0 transition-all duration-300"
                          />
                        ) : (
                          <div className="w-full h-24 bg-gradient-to-br from-[#00B050]/10 to-[#1E4E9E]/10 rounded-lg flex items-center justify-center mb-4">
                            <Globe className="w-12 h-12 text-[#74C65A]/30" />
                          </div>
                        )}
                      </>
                    )}
                    <p className="text-sm font-semibold text-slate-700 text-center">{partner.name}</p>
                    <p className="text-xs text-slate-500 text-center mt-1">{partner.location}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {customers.length === 0 && partners.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-500">No customers or partners available at the moment.</p>
          </div>
        )}
      </div>
    </section>
  );
};
